import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChildProfile } from '../../models/childProfile';
import { ScheduleSession } from './SessionDetailPanel';
import ChildAvatar from '../ChildAvatar';
import { CANCELLATION_REASON_PRESETS } from './CancelDateModal';
import { buildOccurrences, Occurrence } from './scheduleOccurrences';
import { ScheduleDataService, BulkOccurrence, BulkCancelResult } from '../../services/scheduleDataService';
import { AttendanceDataService } from '../../services/attendanceDataService';
import { toDateString, formatDisplayDate, addDays } from '../../services/dateUtils';

// A cancellation/override already on the books, flattened for this modal.
export interface BulkModalException {
  sessionId: string;
  date: string;
  status: 'cancelled' | 'modified';
  serverId?: number;
  reason?: string | null;
}

interface Props {
  open: boolean;
  /** All hydrated rules the current user may see. */
  sessions: ScheduleSession[];
  childProfiles: ChildProfile[];
  /** Local mirror of schedule_exception, keyed by local session id. */
  exceptions: BulkModalException[];
  /** Monday of the week currently on screen — anchors every period preset. */
  weekStart: string;
  /** Weekday columns the grid is showing; the day filter offers exactly these. */
  days: string[];
  /** Sidebar child filter, if any — seeds the child selection. */
  initialChildId: number | null;
  /** Called after a successful write so the parent can re-hydrate exceptions. */
  onApplied: () => void | Promise<void>;
  onClose: () => void;
}

type Mode = 'cancel' | 'restore';
type PeriodPreset = 'this-week' | 'next-week' | 'this-month' | 'custom';

const AVATAR = 26;

// ── Date helpers (local-midnight arithmetic, no UTC drift) ────────────────


function monthBounds(dateStr: string): { from: string; to: string } {
  const d = new Date(dateStr + 'T00:00');
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from: toDateString(first), to: toDateString(last) };
}

function weekCount(from: string, to: string): number {
  const days = Math.round(
    (new Date(to + 'T00:00').getTime() - new Date(from + 'T00:00').getTime()) / 86_400_000,
  ) + 1;
  return Math.max(1, Math.round(days / 7));
}

const byName = (a: ChildProfile, b: ChildProfile) =>
  `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);

/**
 * Bulk period update — cancel or un-cancel every session in a date range.
 *
 * This is deliberately a centred overlay rather than another right-hand panel.
 * The right-hand BulkEditPanel edits *what you have clicked* in the week on
 * screen; this one edits *a period you describe*, which may run for weeks past
 * the visible grid. Composing that (range, children, days, reason) needs room,
 * and the preview needs to be readable before anyone commits to a few hundred
 * writes — so it takes over the screen for as long as it is open.
 */
const BulkPeriodModal: React.FC<Props> = ({
  open, sessions, childProfiles, exceptions, weekStart, days,
  initialChildId, onApplied, onClose,
}) => {
  const [mode, setMode] = useState<Mode>('cancel');
  const [preset, setPreset] = useState<PeriodPreset>('this-week');
  const [customFrom, setCustomFrom] = useState(weekStart);
  const [customTo, setCustomTo] = useState(addDays(weekStart, 6));
  const [weekdays, setWeekdays] = useState<string[]>(days);
  const [allChildren, setAllChildren] = useState(true);
  const [pickedChildIds, setPickedChildIds] = useState<number[]>([]);
  const [childQuery, setChildQuery] = useState('');
  const [reasonPreset, setReasonPreset] = useState('');
  const [reasonFree, setReasonFree] = useState('');
  const [restoreReason, setRestoreReason] = useState('');   // '' = any reason

  type ApplyState =
    | { kind: 'idle' }
    | { kind: 'applying' }
    | { kind: 'cancelled'; result: BulkCancelResult }
    | { kind: 'restored'; count: number; undo: BulkOccurrence[]; reasons: Map<string, string> }
    | { kind: 'undone'; count: number }
    | { kind: 'error'; message: string };
  const [applyState, setApplyState] = useState<ApplyState>({ kind: 'idle' });

  // Attendance for the resolved range, as `${serverId}|${date}` keys. Fetched
  // on a debounce as the range changes so the preview can say up front which
  // occurrences are untouchable, rather than surprising the user afterwards.
  const [attendanceKeys, setAttendanceKeys] = useState<Set<string>>(new Set());
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceFailed, setAttendanceFailed] = useState(false);

  // Reset to a clean sheet each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setMode('cancel');
    setPreset('this-week');
    setCustomFrom(weekStart);
    setCustomTo(addDays(weekStart, 6));
    setWeekdays(days);
    setAllChildren(initialChildId == null);
    setPickedChildIds(initialChildId != null ? [initialChildId] : []);
    setChildQuery('');
    setReasonPreset('');
    setReasonFree('');
    setRestoreReason('');
    setApplyState({ kind: 'idle' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape closes, unless a write is in flight.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && applyState.kind !== 'applying') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, applyState.kind, onClose]);

  // ── Resolved range ──────────────────────────────────────────────────────

  const range = useMemo((): { from: string; to: string } => {
    switch (preset) {
      case 'this-week':  return { from: weekStart, to: addDays(weekStart, 6) };
      case 'next-week':  return { from: addDays(weekStart, 7), to: addDays(weekStart, 13) };
      case 'this-month': return monthBounds(weekStart);
      case 'custom':
      default:           return { from: customFrom, to: customTo };
    }
  }, [preset, weekStart, customFrom, customTo]);

  const rangeInvalid = !range.from || !range.to || range.to < range.from;

  // ── Attendance lookup for the range ─────────────────────────────────────

  const rangeKey = `${range.from}|${range.to}`;
  const attendanceReqRef = useRef(0);
  useEffect(() => {
    if (!open || mode !== 'cancel' || rangeInvalid) return;
    const seq = ++attendanceReqRef.current;
    setAttendanceLoading(true);
    const timer = setTimeout(() => {
      AttendanceDataService.listAttendance(range.from, range.to)
        .then(records => {
          if (seq !== attendanceReqRef.current) return;
          setAttendanceKeys(new Set(
            records.map(r => `${r.scheduleItemServerId}|${r.attendanceDate}`),
          ));
          setAttendanceFailed(false);
          setAttendanceLoading(false);
        })
        .catch(err => {
          if (seq !== attendanceReqRef.current) return;
          // eslint-disable-next-line no-console
          console.error('Bulk period: attendance lookup failed', err);
          setAttendanceKeys(new Set());
          setAttendanceFailed(true);
          setAttendanceLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, rangeKey, rangeInvalid]);

  // ── Preview ─────────────────────────────────────────────────────────────

  const childIdFilter = allChildren ? undefined : pickedChildIds;

  const occurrences = useMemo(
    () => (rangeInvalid ? [] : buildOccurrences(sessions, range.from, range.to, {
      childIds: childIdFilter,
      weekdays,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions, range.from, range.to, allChildren, pickedChildIds.join(','), weekdays.join(','), rangeInvalid],
  );

  // Exceptions indexed by `${sessionId}|${date}` for O(1) classification.
  const exceptionByKey = useMemo(() => {
    const m = new Map<string, BulkModalException>();
    for (const ex of exceptions) m.set(`${ex.sessionId}|${ex.date}`, ex);
    return m;
  }, [exceptions]);

  type Verdict = 'will-cancel' | 'will-convert' | 'already-cancelled' | 'blocked' | 'will-restore';

  const classified = useMemo((): { occ: Occurrence; verdict: Verdict }[] => {
    if (mode === 'cancel') {
      return occurrences.map(occ => {
        const ex = exceptionByKey.get(`${occ.sessionId}|${occ.date}`);
        if (attendanceKeys.has(`${occ.serverId}|${occ.date}`)) return { occ, verdict: 'blocked' as Verdict };
        if (ex?.status === 'cancelled') return { occ, verdict: 'already-cancelled' as Verdict };
        if (ex?.status === 'modified') return { occ, verdict: 'will-convert' as Verdict };
        return { occ, verdict: 'will-cancel' as Verdict };
      });
    }
    // Restore: only occurrences that actually carry a cancellation, narrowed
    // by reason when the user has picked one.
    return occurrences
      .filter(occ => {
        const ex = exceptionByKey.get(`${occ.sessionId}|${occ.date}`);
        if (!ex || ex.status !== 'cancelled') return false;
        if (restoreReason && (ex.reason ?? '') !== restoreReason) return false;
        return true;
      })
      .map(occ => ({ occ, verdict: 'will-restore' as Verdict }));
  }, [mode, occurrences, exceptionByKey, attendanceKeys, restoreReason]);

  const counts = useMemo(() => {
    const c = { willCancel: 0, willConvert: 0, alreadyCancelled: 0, blocked: 0, willRestore: 0 };
    for (const { verdict } of classified) {
      if (verdict === 'will-cancel') c.willCancel++;
      else if (verdict === 'will-convert') c.willConvert++;
      else if (verdict === 'already-cancelled') c.alreadyCancelled++;
      else if (verdict === 'blocked') c.blocked++;
      else if (verdict === 'will-restore') c.willRestore++;
    }
    return c;
  }, [classified]);

  const actionable = mode === 'cancel'
    ? counts.willCancel + counts.willConvert
    : counts.willRestore;

  // Distinct reasons present among the cancellations in range — drives the
  // restore-mode narrowing dropdown from real data rather than the presets,
  // since a reason may have been typed freehand.
  const reasonsInRange = useMemo(() => {
    if (mode !== 'restore') return [] as { reason: string; count: number }[];
    const tally = new Map<string, number>();
    for (const occ of occurrences) {
      const ex = exceptionByKey.get(`${occ.sessionId}|${occ.date}`);
      if (!ex || ex.status !== 'cancelled') continue;
      const r = ex.reason ?? '';
      tally.set(r, (tally.get(r) ?? 0) + 1);
    }
    return Array.from(tally.entries())
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count);
  }, [mode, occurrences, exceptionByKey]);

  // Group the preview by date so it reads as a calendar, not a flat list.
  const grouped = useMemo(() => {
    const byDate = new Map<string, { occ: Occurrence; verdict: Verdict }[]>();
    for (const row of classified) {
      const arr = byDate.get(row.occ.date) ?? [];
      arr.push(row);
      byDate.set(row.occ.date, arr);
    }
    return Array.from(byDate.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [classified]);

  const childById = useMemo(
    () => new Map(childProfiles.map(c => [c.id, c])),
    [childProfiles],
  );

  const sortedChildren = useMemo(() => [...childProfiles].sort(byName), [childProfiles]);
  const visibleChildren = useMemo(() => {
    const q = childQuery.trim().toLowerCase();
    if (!q) return sortedChildren;
    return sortedChildren.filter(c =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      (c.alias ?? '').toLowerCase().includes(q),
    );
  }, [sortedChildren, childQuery]);

  // ── Validation ──────────────────────────────────────────────────────────

  const free = reasonFree.trim();
  const reasonValid =
    (reasonPreset !== '' && reasonPreset !== 'Other') ||
    (reasonPreset === 'Other' && free.length > 0);
  const composedReason = reasonPreset === 'Other'
    ? free
    : free ? `${reasonPreset} — ${free}` : reasonPreset;

  const childSelectionValid = allChildren || pickedChildIds.length > 0;

  const canApply =
    applyState.kind !== 'applying' &&
    !rangeInvalid &&
    childSelectionValid &&
    actionable > 0 &&
    (mode === 'restore' || (reasonValid && !attendanceLoading));

  // ── Apply ───────────────────────────────────────────────────────────────

  const handleApplyCancel = async () => {
    const targets: BulkOccurrence[] = classified
      .filter(r => r.verdict === 'will-cancel' || r.verdict === 'will-convert')
      .map(r => ({ scheduleItemServerId: r.occ.serverId, date: r.occ.date }));
    if (targets.length === 0) return;
    setApplyState({ kind: 'applying' });
    try {
      const result = await ScheduleDataService.bulkCancelOccurrences(targets, composedReason);
      await onApplied();
      setApplyState({ kind: 'cancelled', result });
    } catch (err) {
      setApplyState({ kind: 'error', message: errorText(err, 'Failed to cancel the selected sessions.') });
    }
  };

  const handleApplyRestore = async () => {
    const rows = classified
      .map(r => ({
        occ: r.occ,
        ex: exceptionByKey.get(`${r.occ.sessionId}|${r.occ.date}`),
      }))
      .filter(r => r.ex?.serverId !== undefined);
    if (rows.length === 0) return;
    setApplyState({ kind: 'applying' });
    try {
      const ids = rows.map(r => r.ex!.serverId!);
      const { deleted } = await ScheduleDataService.bulkDeleteExceptions(ids);
      // Keep enough to put them back: the (rule, date) pairs and the reason
      // each one carried, since Undo has to re-cancel under the original.
      const undo: BulkOccurrence[] = rows.map(r => ({
        scheduleItemServerId: r.occ.serverId,
        date: r.occ.date,
      }));
      const reasons = new Map<string, string>();
      for (const r of rows) {
        reasons.set(`${r.occ.serverId}|${r.occ.date}`, r.ex?.reason?.trim() || 'Cancelled');
      }
      await onApplied();
      setApplyState({ kind: 'restored', count: deleted, undo, reasons });
    } catch (err) {
      setApplyState({ kind: 'error', message: errorText(err, 'Failed to restore the selected sessions.') });
    }
  };

  // Undo a cancel: delete exactly the rows that call wrote. Rows that were
  // already cancelled beforehand aren't in the list, so they stay cancelled.
  const handleUndoCancel = async (result: BulkCancelResult) => {
    setApplyState({ kind: 'applying' });
    try {
      const { deleted } = await ScheduleDataService.bulkDeleteExceptions(result.exceptionIds);
      await onApplied();
      setApplyState({ kind: 'undone', count: deleted });
    } catch (err) {
      setApplyState({ kind: 'error', message: errorText(err, 'Failed to undo.') });
    }
  };

  // Undo a restore: re-cancel, grouped by the reason each row originally
  // carried (one call per distinct reason — normally one or two). A row whose
  // original reason was blank — only possible for rows predating the
  // mandatory-reason rule — comes back as a plain "Cancelled".
  const handleUndoRestore = async (undo: BulkOccurrence[], reasons: Map<string, string>) => {
    setApplyState({ kind: 'applying' });
    try {
      const groups = new Map<string, BulkOccurrence[]>();
      for (const occ of undo) {
        const reason = reasons.get(`${occ.scheduleItemServerId}|${occ.date}`) || 'Cancelled';
        const arr = groups.get(reason) ?? [];
        arr.push(occ);
        groups.set(reason, arr);
      }
      let total = 0;
      for (const [reason, occs] of Array.from(groups.entries())) {
        const result = await ScheduleDataService.bulkCancelOccurrences(occs, reason);
        total += result.created + result.converted;
      }
      await onApplied();
      setApplyState({ kind: 'undone', count: total });
    } catch (err) {
      setApplyState({ kind: 'error', message: errorText(err, 'Failed to undo.') });
    }
  };

  if (!open) return null;

  const busy = applyState.kind === 'applying';
  const rangeLabel = rangeInvalid
    ? 'Choose an end date on or after the start date'
    : `${formatDisplayDate(range.from)} – ${formatDisplayDate(range.to)} · ${weekCount(range.from, range.to)} ${weekCount(range.from, range.to) === 1 ? 'week' : 'weeks'}`;

  const toggleWeekday = (day: string) =>
    setWeekdays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));

  const toggleChild = (id: number) =>
    setPickedChildIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const choosePreset = (p: PeriodPreset) => {
    // Seed the custom inputs from whatever was on screen, so switching to
    // Custom starts from the range the user was already looking at.
    if (p === 'custom') {
      setCustomFrom(range.from);
      setCustomTo(range.to);
    }
    setPreset(p);
  };

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wsg-bulk-title"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered wsg-bulk__dialog">
        <div className="modal-content ivory-panel" onMouseDown={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h5 id="wsg-bulk-title" className="modal-title d-flex align-items-center gap-2">
                <i className="fas fa-calendar-xmark" style={{ color: '#a86a00', fontSize: '0.95rem' }} aria-hidden />
                Bulk update sessions
              </h5>
              <div className="wsg-bulk__sub">
                Cancel or restore every session in a period — across weeks, for the children you choose.
              </div>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={busy} />
          </div>

          <div className="modal-body wsg-bulk__body">
            <div className="wsg-bulk__cols">
              {/* ── Left: what to do ─────────────────────────────────── */}
              <div className="wsg-bulk__controls">
                <section className="wsg-bulk__section">
                  <span className="wsg-bulk__label">Action</span>
                  <div className="wsg-bulk__segmented" role="radiogroup" aria-label="Action">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={mode === 'cancel'}
                      className={`wsg-bulk__seg${mode === 'cancel' ? ' is-on' : ''}`}
                      onClick={() => { setMode('cancel'); setApplyState({ kind: 'idle' }); }}
                    >
                      <i className="fas fa-ban" aria-hidden /> Cancel sessions
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={mode === 'restore'}
                      className={`wsg-bulk__seg${mode === 'restore' ? ' is-on' : ''}`}
                      onClick={() => { setMode('restore'); setApplyState({ kind: 'idle' }); }}
                    >
                      <i className="fas fa-rotate-left" aria-hidden /> Restore cancelled
                    </button>
                  </div>
                </section>

                <section className="wsg-bulk__section">
                  <span className="wsg-bulk__label">Period</span>
                  <div className="wsg-bulk__chips">
                    {([
                      ['this-week', 'This week'],
                      ['next-week', 'Next week'],
                      ['this-month', 'This month'],
                      ['custom', 'Custom range'],
                    ] as [PeriodPreset, string][]).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={`wsg-bulk__chip${preset === value ? ' is-on' : ''}`}
                        onClick={() => choosePreset(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {preset === 'custom' && (
                    <div className="wsg-bulk__daterow">
                      <label>
                        <span>From</span>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={customFrom}
                          onChange={e => setCustomFrom(e.target.value)}
                        />
                      </label>
                      <label>
                        <span>To</span>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={customTo}
                          min={customFrom}
                          onChange={e => setCustomTo(e.target.value)}
                        />
                      </label>
                    </div>
                  )}
                  <p className={`wsg-bulk__resolved${rangeInvalid ? ' is-bad' : ''}`}>{rangeLabel}</p>
                </section>

                <section className="wsg-bulk__section">
                  <span className="wsg-bulk__label">
                    Days
                    {weekdays.length !== days.length && (
                      <button type="button" className="wsg-bulk__linkbtn" onClick={() => setWeekdays(days)}>
                        reset
                      </button>
                    )}
                  </span>
                  <div className="wsg-bulk__chips">
                    {days.map(day => (
                      <button
                        key={day}
                        type="button"
                        className={`wsg-bulk__chip wsg-bulk__chip--day${weekdays.includes(day) ? ' is-on' : ''}`}
                        onClick={() => toggleWeekday(day)}
                        aria-pressed={weekdays.includes(day)}
                      >
                        {day.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="wsg-bulk__section">
                  <span className="wsg-bulk__label">Children</span>
                  <div className="wsg-bulk__segmented" role="radiogroup" aria-label="Children">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={allChildren}
                      className={`wsg-bulk__seg${allChildren ? ' is-on' : ''}`}
                      onClick={() => setAllChildren(true)}
                    >
                      All children
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!allChildren}
                      className={`wsg-bulk__seg${!allChildren ? ' is-on' : ''}`}
                      onClick={() => setAllChildren(false)}
                    >
                      Choose{pickedChildIds.length > 0 ? ` (${pickedChildIds.length})` : ''}
                    </button>
                  </div>

                  {!allChildren && (
                    <div className="wsg-bulk__childpicker">
                      <div className="wsg-bulk__search">
                        <i className="fas fa-search" aria-hidden />
                        <input
                          type="search"
                          value={childQuery}
                          onChange={e => setChildQuery(e.target.value)}
                          placeholder="Search children"
                          aria-label="Search children"
                        />
                      </div>
                      <div className="wsg-bulk__childlist">
                        {visibleChildren.length === 0 ? (
                          <p className="wsg-bulk__empty">No children match that search.</p>
                        ) : visibleChildren.map(c => {
                          const picked = pickedChildIds.includes(c.id);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              className={`wsg-bulk__childrow${picked ? ' is-picked' : ''}`}
                              onClick={() => toggleChild(c.id)}
                              aria-pressed={picked}
                            >
                              <ChildAvatar
                                child={c}
                                size={AVATAR}
                                imageUrl={c.profile_image_thumbnail_url || c.profile_image_url || null}
                                showBadge={false}
                              />
                              <span className="wsg-bulk__childname">{c.first_name} {c.last_name}</span>
                              <i className={`fas ${picked ? 'fa-check' : 'fa-plus'}`} aria-hidden />
                            </button>
                          );
                        })}
                      </div>
                      {pickedChildIds.length === 0 && (
                        <p className="wsg-bulk__hint">Pick at least one child.</p>
                      )}
                    </div>
                  )}
                </section>

                {mode === 'cancel' ? (
                  <section className="wsg-bulk__section">
                    <span className="wsg-bulk__label">
                      Reason <span style={{ color: '#b8302f' }}>*</span>
                    </span>
                    <select
                      className="form-select form-select-sm"
                      value={reasonPreset}
                      onChange={e => setReasonPreset(e.target.value)}
                    >
                      <option value="">Select a reason…</option>
                      {CANCELLATION_REASON_PRESETS.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <textarea
                      className="form-control form-control-sm mt-2"
                      value={reasonFree}
                      onChange={e => setReasonFree(e.target.value)}
                      rows={2}
                      maxLength={500}
                      placeholder={reasonPreset === 'Other' ? 'Describe the reason (required)' : 'Additional notes (optional)'}
                      style={{ resize: 'vertical' }}
                    />
                    <p className="wsg-bulk__hint">Written against every session cancelled here.</p>
                  </section>
                ) : (
                  <section className="wsg-bulk__section">
                    <span className="wsg-bulk__label">Cancelled for</span>
                    <select
                      className="form-select form-select-sm"
                      value={restoreReason}
                      onChange={e => setRestoreReason(e.target.value)}
                    >
                      <option value="">Any reason</option>
                      {reasonsInRange.map(({ reason, count }) => (
                        <option key={reason || '(none)'} value={reason}>
                          {reason || 'No reason recorded'} ({count})
                        </option>
                      ))}
                    </select>
                    <p className="wsg-bulk__hint">
                      Narrow the restore to sessions cancelled for one reason — useful when
                      only part of a closure is being reversed.
                    </p>
                  </section>
                )}
              </div>

              {/* ── Right: preview ───────────────────────────────────── */}
              <div className="wsg-bulk__preview">
                <header className="wsg-bulk__preview-head">
                  <span className="wsg-bulk__label">Preview</span>
                  {attendanceLoading && mode === 'cancel' && (
                    <span className="wsg-bulk__loading">
                      <i className="fas fa-spinner fa-spin" aria-hidden /> checking attendance…
                    </span>
                  )}
                </header>

                <div className="wsg-bulk__counters">
                  <div className="wsg-bulk__counter wsg-bulk__counter--primary">
                    <strong>{actionable}</strong>
                    <span>{mode === 'cancel' ? 'to cancel' : 'to restore'}</span>
                  </div>
                  {mode === 'cancel' && counts.alreadyCancelled > 0 && (
                    <div className="wsg-bulk__counter">
                      <strong>{counts.alreadyCancelled}</strong>
                      <span>already cancelled</span>
                    </div>
                  )}
                  {mode === 'cancel' && counts.blocked > 0 && (
                    <div className="wsg-bulk__counter wsg-bulk__counter--warn">
                      <strong>{counts.blocked}</strong>
                      <span>attendance recorded</span>
                    </div>
                  )}
                  {mode === 'cancel' && counts.willConvert > 0 && (
                    <div className="wsg-bulk__counter wsg-bulk__counter--warn">
                      <strong>{counts.willConvert}</strong>
                      <span>one-off change replaced</span>
                    </div>
                  )}
                </div>

                {mode === 'cancel' && attendanceFailed && (
                  <div className="wsg-bulk__note wsg-bulk__note--warn" role="alert">
                    <i className="fas fa-triangle-exclamation" aria-hidden />
                    Couldn't check attendance records. Sessions with attendance will be
                    refused when you apply, and listed in the result.
                  </div>
                )}
                {mode === 'cancel' && counts.blocked > 0 && (
                  <div className="wsg-bulk__note">
                    <i className="fas fa-circle-info" aria-hidden />
                    Sessions with attendance already recorded are skipped — clear the
                    record on the Attendance page to cancel those.
                  </div>
                )}

                <div className="wsg-bulk__list">
                  {rangeInvalid ? (
                    <p className="wsg-bulk__empty">Choose a valid date range.</p>
                  ) : grouped.length === 0 ? (
                    <p className="wsg-bulk__empty">
                      {mode === 'cancel'
                        ? 'No sessions fall in this period for the current selection.'
                        : 'No cancelled sessions in this period for the current selection.'}
                    </p>
                  ) : grouped.map(([date, rows]) => (
                    <div key={date} className="wsg-bulk__daygroup">
                      <div className="wsg-bulk__dayhead">
                        <span className="wsg-bulk__daydate">{formatDisplayDate(date)}</span>
                        <span className="wsg-bulk__daycount">
                          {rows.length} {rows.length === 1 ? 'session' : 'sessions'}
                        </span>
                      </div>
                      {rows.map(({ occ, verdict }) => {
                        const c = childById.get(occ.childId);
                        return (
                          <div
                            key={`${occ.sessionId}|${occ.date}`}
                            className={`wsg-bulk__row is-${verdict}`}
                          >
                            <span
                              className="wsg-bulk__dot"
                              style={{ background: c?.colour_code || '#aaa' }}
                              aria-hidden
                            />
                            <span className="wsg-bulk__rowname">
                              {c ? `${c.first_name} ${c.last_name}` : 'Unassigned'}
                            </span>
                            <span className="wsg-bulk__rowtime">{occ.startTime}–{occ.endTime}</span>
                            {verdict === 'blocked' && (
                              <span className="wsg-bulk__tag wsg-bulk__tag--warn">attendance</span>
                            )}
                            {verdict === 'already-cancelled' && (
                              <span className="wsg-bulk__tag">already cancelled</span>
                            )}
                            {verdict === 'will-convert' && (
                              <span className="wsg-bulk__tag wsg-bulk__tag--warn">replaces one-off change</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer wsg-bulk__footer">
            {applyState.kind === 'error' && (
              <div className="wsg-bulk__result wsg-bulk__result--bad" role="alert">
                <i className="fas fa-circle-exclamation" aria-hidden />
                <span>{applyState.message}</span>
              </div>
            )}
            {applyState.kind === 'cancelled' && (
              <div className="wsg-bulk__result" role="status">
                <i className="fas fa-check-circle" aria-hidden />
                <span>
                  Cancelled {applyState.result.created + applyState.result.converted}{' '}
                  {applyState.result.created + applyState.result.converted === 1 ? 'session' : 'sessions'}.
                  {applyState.result.skippedAttendance.length > 0 &&
                    ` ${applyState.result.skippedAttendance.length} skipped — attendance recorded.`}
                  {applyState.result.alreadyCancelled > 0 &&
                    ` ${applyState.result.alreadyCancelled} were already cancelled.`}
                </span>
                {applyState.result.exceptionIds.length > 0 && (
                  <button
                    type="button"
                    className="wsg-bulk__undo"
                    onClick={() => handleUndoCancel(applyState.result)}
                  >
                    <i className="fas fa-rotate-left" aria-hidden /> Undo
                  </button>
                )}
              </div>
            )}
            {applyState.kind === 'restored' && (
              <div className="wsg-bulk__result" role="status">
                <i className="fas fa-check-circle" aria-hidden />
                <span>Restored {applyState.count} {applyState.count === 1 ? 'session' : 'sessions'}.</span>
                {applyState.undo.length > 0 && (
                  <button
                    type="button"
                    className="wsg-bulk__undo"
                    onClick={() => handleUndoRestore(applyState.undo, applyState.reasons)}
                  >
                    <i className="fas fa-rotate-left" aria-hidden /> Undo
                  </button>
                )}
              </div>
            )}
            {applyState.kind === 'undone' && (
              <div className="wsg-bulk__result" role="status">
                <i className="fas fa-check-circle" aria-hidden />
                <span>Undone — {applyState.count} {applyState.count === 1 ? 'session' : 'sessions'} put back.</span>
              </div>
            )}

            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
              Close
            </button>
            <button
              type="button"
              className={`btn ${mode === 'cancel' ? 'btn-warning' : 'btn-primary'} d-inline-flex align-items-center gap-2`}
              onClick={mode === 'cancel' ? handleApplyCancel : handleApplyRestore}
              disabled={!canApply}
            >
              {busy
                ? <><i className="fas fa-spinner fa-spin" aria-hidden />Working…</>
                : mode === 'cancel'
                  ? <><i className="fas fa-ban" aria-hidden />Cancel {actionable} {actionable === 1 ? 'session' : 'sessions'}</>
                  : <><i className="fas fa-rotate-left" aria-hidden />Restore {actionable} {actionable === 1 ? 'session' : 'sessions'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function errorText(err: unknown, fallback: string): string {
  const apiMessage = (err as any)?.response?.data?.error;
  if (typeof apiMessage === 'string' && apiMessage) return apiMessage;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export default BulkPeriodModal;
